import React from "react";
import { tattoo } from "../../state";
import LayersPanel from "../../LayersPanel.jsx";
import ExportPreviewButton from "../../ExportPreviewButton";
import { shareOrDownloadFile } from "../../lib/shareFile";
import ToolCard from "../../components/ToolCard";
import { openTextDialog } from "../../components/TextLayerDialog.jsx";

function presetLabel(preset) {
  if (preset === "sweepRight") return "Sweep Right";
  if (preset === "sweepLeft") return "Sweep Left";
  return "Slow Push";
}

export default function LayersSection({
  layerFileRef, decalFileRef, onUploadLayer, onUploadDecal,
  // onOpenArtistWizard is the gated handler from useLeftPanelState that
  // runs the trial/paywall check before opening the wizard. setShowArtistWizard
  // is kept as a fallback for any caller that hasn't been migrated yet.
  setShowArtistWizard, onOpenArtistWizard,
  shopProfileLoading, hasShopProfile, shopNeedsReactivation,
  readOnly,
  onExportPreview, onExportPreviewVideo,
  preparedVideo, setPreparedVideo, videoBusy, setVideoBusy,
  videoPreset, setVideoPreset,
  layersPartKey, uiLayers,
  variant = "desktop",
}) {
  return (
    <>
      <div className="section">
        <div className="label">Designs</div>
        <div className="tt-row" style={{ flexWrap: "wrap", gap: 8 }}>
          <label className="tt-btn tt-btn--fill-primary" style={{ cursor: "pointer" }}>
            <input
              ref={layerFileRef}
              type="file"
              accept="image/*"
              onChange={onUploadLayer}
              style={{ display: "none" }}
              aria-label="Upload full design image"
            />
            Upload Full Design
          </label>
          <label className="tt-btn" style={{ cursor: "pointer" }}>
            <input
              ref={decalFileRef}
              type="file"
              accept="image/*"
              onChange={onUploadDecal}
              style={{ display: "none" }}
              aria-label="Add image decal"
            />
            Add Image
          </label>
          {!readOnly && (
            <button
              type="button"
              className="tt-btn"
              onClick={() => openTextDialog()}
              aria-label="Add text layer"
            >
              Add Text
            </button>
          )}
          <button
            type="button"
            className="tt-btn"
            style={{ borderColor: "#a2332b", color: "#f5f5f5", whiteSpace: "nowrap" }}
            onClick={() => {
              if (onOpenArtistWizard) {
                onOpenArtistWizard();
              } else if (setShowArtistWizard) {
                setShowArtistWizard(true);
              }
            }}
            disabled={shopProfileLoading}
          >
            {hasShopProfile
              ? shopNeedsReactivation
                ? variant === "desktop" ? "Reactivate studio profile" : "Reactivate profile"
                : variant === "desktop" ? "Edit studio profile" : "Edit profile"
              : "Become a Tattune artist (artists)"}
          </button>
        </div>
      </div>

      {!readOnly && (
        <ToolCard
          title="Social Exports"
          subtitle="Create branded stills and reels for Instagram and TikTok."
        >
          <div className="section" style={{ marginTop: -4, marginBottom: 4 }}>
            <div className="tt-row" style={{ alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              <div className="tt-row" style={{ gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <ExportPreviewButton
                      onExportPreview={onExportPreview}
                      designName={tattoo.layers?.[0]?.name || "Tattoo Preview"}
                      artistName={tattoo.layers?.[0]?.ownerShopName || ""}
                    />
                    <button
                      type="button"
                      className="tt-btn"
                      disabled={videoBusy}
                      onClick={async () => {
                        if (videoBusy) return;
                        setVideoBusy(true);
                        try {
                          const result = await onExportPreviewVideo?.({ preset: videoPreset });
                          if (!result?.blob) return;
                          const ext = result.mimeType?.includes("mp4") ? "mp4" : "webm";
                          setPreparedVideo({
                            blob: result.blob,
                            mimeType: result.mimeType,
                            filename: `tattune-${videoPreset}.${ext}`,
                            preset: videoPreset,
                          });
                        } catch (err) {
                          console.warn("[video export] prepare failed", err);
                        } finally {
                          setVideoBusy(false);
                        }
                      }}
                    >
                      {videoBusy
                        ? `Preparing ${presetLabel(videoPreset)}…`
                        : preparedVideo?.blob
                        ? "Reel Ready ✓"
                        : `Prepare ${presetLabel(videoPreset)}`}
                    </button>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
                    <button
                      type="button"
                      className="tt-btn"
                      onClick={() => setVideoPreset("push")}
                      disabled={videoBusy}
                      style={{ opacity: videoPreset === "push" ? 1 : 0.72, borderColor: videoPreset === "push" ? "#a2332b" : undefined }}
                    >
                      Push
                    </button>
                    <button
                      type="button"
                      className="tt-btn"
                      onClick={() => setVideoPreset("sweepRight")}
                      disabled={videoBusy}
                      style={{ opacity: videoPreset === "sweepRight" ? 1 : 0.72, borderColor: videoPreset === "sweepRight" ? "#a2332b" : undefined }}
                    >
                      Sweep →
                    </button>
                    <button
                      type="button"
                      className="tt-btn"
                      onClick={() => setVideoPreset("sweepLeft")}
                      disabled={videoBusy}
                      style={{ opacity: videoPreset === "sweepLeft" ? 1 : 0.72, borderColor: videoPreset === "sweepLeft" ? "#a2332b" : undefined }}
                    >
                      ← Sweep
                    </button>
                  </div>

                  {preparedVideo?.blob && (
                    <button
                      type="button"
                      className="tt-btn"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        try {
                          await shareOrDownloadFile({
                            blob: preparedVideo.blob,
                            filename: preparedVideo.filename,
                            title: `Tattune Reel — ${presetLabel(preparedVideo.preset)}`,
                            text: tattoo.layers?.[0]?.name || "Tattune Reel",
                          });
                        } catch (err) {
                          console.warn("[video export] share failed", err);
                        }
                      }}
                    >
                      Share Reel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </ToolCard>
      )}

      <div className="section">
        <LayersPanel partKey={layersPartKey} layers={uiLayers} />
      </div>
    </>
  );
}
